import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, FILE_URL, formatBRL, displayBrand } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import PaymentBrick from "../components/PaymentBrick";
import DiscountSection from "../components/DiscountSection";
import TrustBadges from "../components/TrustBadges";
import ConsultoraModal from "../components/ConsultoraModal";
import { usePixDiscount } from "../lib/pixDiscount";

// Mask + checksum for Brazilian CPF
const maskCpf = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};
const isValidCpf = (raw) => {
  const c = (raw || "").replace(/\D/g, "");
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  for (const j of [9, 10]) {
    let total = 0;
    for (let i = 0; i < j; i++) total += parseInt(c[i], 10) * (j + 1 - i);
    let d = (total * 10) % 11;
    if (d === 10) d = 0;
    if (d !== parseInt(c[j], 10)) return false;
  }
  return true;
};

// Mask for Brazilian phone: (XX) XXXXX-XXXX (mobile) or (XX) XXXX-XXXX (landline)
const maskPhone = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export default function CheckoutPage() {
  const { items, subtotal, count, loading } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({
    name: "", email: "", cep: "", street: "", number: "", complement: "",
    neighborhood: "", city: "", state: "", phone: "", cpf: "",
  });
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState("");
  const [shippingEstimate, setShippingEstimate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("address"); // "address" | "payment" | "approved" | "pending"
  const [intent, setIntent] = useState(null);
  const [totals, setTotals] = useState(null); // {original_amount, coupon_*, giftcard_*, final_amount}
  const [paymentResult, setPaymentResult] = useState(null);
  const [gcFinalizing, setGcFinalizing] = useState(false);
  const [consultoraOpen, setConsultoraOpen] = useState(false);
  // PIX à-vista discount — tabs above the Brick let the customer pre-pick
  // the method (MP's vanilla JS SDK does not expose a reliable per-method
  // selection callback, so we drive the selection ourselves and remount
  // the Brick with the right amount + restricted paymentMethods).
  const pixDiscountPct = usePixDiscount();
  const [selectedPaymentType, setSelectedPaymentType] = useState("credit_card");
  const isPixSelected = selectedPaymentType === "bank_transfer";
  // Snapshot the email used at checkout so the "Criar conta" CTA on the
  // approved screen can pre-fill the register form for guest buyers.
  const [checkoutEmail, setCheckoutEmail] = useState("");

  useEffect(() => {
    if (!loading && count === 0 && step === "address") {
      nav("/joias/todos", { replace: true });
    }
  }, [loading, count, nav, step]);

  // Guest checkout allowed: no auth gate. Users are prompted to create an
  // account AFTER approval (see approved step below), so nothing blocks
  // the buying flow if they arrived here from a "Comprar" CTA without login.

  // Pre-fill form from logged-in user's profile (name, email, phone, CPF if saved)
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || maskPhone(user.phone || ""),
        cpf: f.cpf || maskCpf(user.cpf || ""),
      }));
    }
  }, [user]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const maskCep = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 8);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
  };

  const handleCepChange = async (e) => {
    const masked = maskCep(e.target.value);
    setForm((f) => ({ ...f, cep: masked }));
    const digits = masked.replace(/\D/g, "");
    if (digits.length !== 8) { setShippingEstimate(null); return; }
    setCepLoading(true);
    setCepError("");
    try {
      const [via, est] = await Promise.all([
        fetch(`https://viacep.com.br/ws/${digits}/json/`).then((r) => r.json()),
        api.get(`/mp/shipping-estimate?cep=${digits}`).then((r) => r.data).catch(() => null),
      ]);
      if (via?.erro) {
        setCepError("CEP não encontrado. Verifique e tente novamente.");
      } else {
        setForm((f) => ({
          ...f,
          street: via.logradouro || f.street,
          neighborhood: via.bairro || f.neighborhood,
          city: via.localidade || "",
          state: via.uf || "",
        }));
      }
      if (est) setShippingEstimate(est);
    } catch {
      setCepError("Não foi possível buscar o CEP agora. Preencha manualmente.");
    } finally {
      setCepLoading(false);
    }
  };

  const submitAddress = async (e) => {
    e.preventDefault();
    setError("");
    // Guest checkout: email must come from the form when the user isn't logged in.
    const emailToUse = (user?.email || form.email || "").trim().toLowerCase();
    if (!emailToUse || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToUse)) {
      setError("Informe um e-mail válido.");
      return;
    }
    if (!isValidCpf(form.cpf)) { setError("CPF inválido. Verifique os 11 dígitos."); return; }
    const phoneDigits = (form.phone || "").replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 13) {
      setError("Informe um telefone válido com DDD (mínimo 10 dígitos).");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/mp/checkout/init", {
        kind: "jewelry",
        cpf: form.cpf.replace(/\D/g, ""),
        customer_email: emailToUse,
        customer_name: form.name.trim(),
        shipping: {
          name: form.name.trim(),
          cep: form.cep.replace(/\D/g, ""),
          street: form.street.trim(),
          number: form.number.trim(),
          complement: form.complement.trim(),
          neighborhood: form.neighborhood.trim(),
          city: form.city.trim(),
          state: form.state.trim().toUpperCase(),
          phone: form.phone.trim(),
        },
      }, { withCredentials: true });
      setIntent(r.data);
      setTotals(r.data.totals || {
        original_amount: r.data.amount,
        coupon_code: null, coupon_discount: 0,
        giftcard_code: null, giftcard_used: 0,
        final_amount: r.data.amount,
      });
      setCheckoutEmail(emailToUse);
      setStep("payment");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => x?.msg || JSON.stringify(x)).join("; ")
        : (typeof d === "string" ? d : (d?.message || "Não foi possível iniciar o checkout."));
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || (count === 0 && step === "address")) {
    return <div className="p-20 text-center text-[#A8A8A8]">Carregando…</div>;
  }

  return (
    <div className="bg-[#FAF9F7] min-h-screen pb-24" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-12">
        <div className="text-center mb-12">
          <p className="overline text-[#B8A88A] mb-4">Finalize seu pedido</p>
          <h1 className="font-display text-4xl md:text-5xl">Checkout</h1>
        </div>

        <div className={step === "approved" ? "grid grid-cols-1" : "grid md:grid-cols-[1fr_380px] gap-10"}>
          <div>
            {step === "address" && (
              <form onSubmit={submitAddress} className="bg-white p-8 md:p-10 space-y-4" data-testid="checkout-form">
                <h2 className="font-display text-2xl mb-4">Endereço de entrega</h2>
                <input className="input" placeholder="Nome completo" value={form.name} onChange={setField("name")} required data-testid="checkout-name"/>
                {!user && (
                  <input
                    className="input"
                    type="email"
                    placeholder="E-mail para receber a confirmação"
                    value={form.email}
                    onChange={setField("email")}
                    required
                    data-testid="checkout-email"
                    autoComplete="email"
                  />
                )}
                <input className="input" placeholder="CPF" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: maskCpf(e.target.value) })} required data-testid="checkout-cpf" inputMode="numeric"/>
                <input className="input" placeholder="CEP" value={form.cep} onChange={handleCepChange} required data-testid="checkout-cep" inputMode="numeric"/>
                {cepLoading && <p className="text-xs text-[#A8A8A8]">Buscando CEP…</p>}
                {cepError && <p className="text-xs text-[#A8503B]">{cepError}</p>}
                <div className="grid grid-cols-[1fr_120px] gap-3">
                  <input className="input" placeholder="Rua" value={form.street} onChange={setField("street")} required/>
                  <input className="input" placeholder="Número" value={form.number} onChange={setField("number")} required/>
                </div>
                <input className="input" placeholder="Complemento" value={form.complement} onChange={setField("complement")}/>
                <input className="input" placeholder="Bairro" value={form.neighborhood} onChange={setField("neighborhood")} required/>
                <div className="grid grid-cols-[1fr_90px] gap-3">
                  <input className="input" placeholder="Cidade" value={form.city} onChange={setField("city")} required/>
                  <input className="input" placeholder="UF" value={form.state} onChange={setField("state")} maxLength={2} required/>
                </div>
                <input className="input" placeholder="Telefone com DDD" value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })} required data-testid="checkout-phone" inputMode="tel"/>
                {error && <p className="text-sm text-[#A8503B]">{error}</p>}
                <button type="submit" disabled={submitting} className="btn-primary w-full mt-4" data-testid="checkout-continue-btn">
                  {submitting ? "Aguarde…" : "Continuar para pagamento"}
                </button>
              </form>
            )}

            {step === "payment" && intent && (
              <div className="bg-white p-6 md:p-8" data-testid="checkout-payment-step">
                <h2 className="font-display text-2xl mb-6">Confirme seu meio de pagamento</h2>

                {/* Discount inputs — coupon OR gift card (mutually exclusive) */}
                <div className="mb-8 pb-8 border-b border-[#E8E4DF]">
                  <DiscountSection
                    intentId={intent.intent_id}
                    totals={totals}
                    onTotalsChange={(resp) => {
                      setTotals(resp.totals);
                      // Update intent.amount so the Brick re-mounts with the new value.
                      setIntent((prev) => (prev ? { ...prev, amount: resp.amount } : prev));
                    }}
                  />
                </div>

                {totals && totals.final_amount === 0 && totals.giftcard_code ? (
                  <div className="text-center space-y-6" data-testid="gc-full-coverage">
                    <div className="p-6 border border-[#B5D0A0] bg-[#F1F7EC]">
                      <p className="overline text-[#3E6A1E] mb-3">Gift card cobre 100%</p>
                      <p className="text-sm text-[#3E6A1E] leading-relaxed">
                        Este pedido está totalmente coberto pelo gift card{" "}
                        <strong>{totals.giftcard_code}</strong>.
                        <br />
                        Nenhum pagamento adicional é necessário.
                      </p>
                    </div>
                    {error && <p className="text-sm text-[#A8503B]">{error}</p>}
                    <button
                      type="button"
                      disabled={gcFinalizing}
                      onClick={async () => {
                        setGcFinalizing(true);
                        setError("");
                        try {
                          const r = await api.post(
                            `/mp/checkout/${intent.intent_id}/complete-giftcard`
                          );
                          setPaymentResult(r.data);
                          setStep("approved");
                        } catch (err) {
                          const d = err?.response?.data?.detail;
                          setError(
                            typeof d === "string"
                              ? d
                              : d?.message || "Não foi possível finalizar. Tente novamente."
                          );
                        } finally {
                          setGcFinalizing(false);
                        }
                      }}
                      className="btn-primary w-full"
                      data-testid="gc-finalize-btn"
                    >
                      {gcFinalizing ? "Finalizando…" : "Confirmar pedido"}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Payment method tabs — MP's vanilla JS SDK does NOT
                        expose an onPaymentMethodSelected callback in
                        `callbacks:{}`, so relying on it (previous approach)
                        never fired. We pre-select the method OUTSIDE the
                        Brick and remount it with the corresponding
                        `paymentMethods` config + discounted amount. The
                        sidebar summary reacts to `selectedPaymentType`
                        immediately, before the Brick even loads. */}
                    {pixDiscountPct > 0 && (
                      <div className="mb-6 flex gap-3" data-testid="payment-method-tabs">
                        {[
                          { key: "credit_card", label: "Cartão de crédito" },
                          { key: "bank_transfer", label: `PIX · ${pixDiscountPct}% off` },
                        ].map((tab) => {
                          const active =
                            (selectedPaymentType || "credit_card") === tab.key;
                          return (
                            <button
                              key={tab.key}
                              type="button"
                              onClick={() => setSelectedPaymentType(tab.key)}
                              className={`flex-1 py-3 px-4 text-sm border transition-colors ${
                                active
                                  ? "border-[#1A1A1A] bg-[#1A1A1A] text-white"
                                  : "border-[#E8E4DF] bg-white text-[#1A1A1A] hover:border-[#B8A88A]"
                              }`}
                              data-testid={`payment-tab-${tab.key}`}
                            >
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <PaymentBrick
                      key={selectedPaymentType || "default"}
                      intentId={intent.intent_id}
                      amount={(() => {
                        const base = Number(intent.amount) || 0;
                        return isPixSelected && pixDiscountPct > 0
                          ? Math.round(base * (1 - pixDiscountPct / 100) * 100) / 100
                          : base;
                      })()}
                      publicKey={intent.public_key}
                      payerEmail={intent.payer_email}
                      restrictTo={selectedPaymentType}
                      onApproved={(r) => { setPaymentResult(r); setError(""); setStep("approved"); }}
                      onPending={(r) => { setPaymentResult(r); setError(""); setStep("pending"); }}
                      onRejected={(r) => {
                        // PaymentBrick owns the inline error UI — we just hold on
                        // to the result for debugging / future "view receipt" UX.
                        // The 15-min reservation lock stays intact (only the
                        // approved branch clears reservations server-side).
                        setPaymentResult(r);
                      }}
                      onRequestHelp={() => setConsultoraOpen(true)}
                    />
                    {/* Security copy — sits BELOW the Brick (boleto removed in
                        PaymentBrick customization so we only mention cartão + PIX). */}
                    <p className="text-xs text-[#A8A8A8] mt-4 leading-relaxed text-center" data-testid="checkout-security-note">
                      Cartão de crédito ou PIX. Pagamento processado pelo Mercado Pago — seus dados não passam pelo nosso servidor.
                    </p>
                  </>
                )}
                {/* "Iniciar novo pedido" — used when the intent was already processed
                    in a prior attempt and the customer needs a fresh one. */}
                <div className="mt-6 pt-6 border-t border-[#E8E4DF] text-center">
                  <button
                    type="button"
                    onClick={() => { setIntent(null); setTotals(null); setPaymentResult(null); setStep("address"); setError(""); }}
                    className="text-xs uppercase tracking-wider text-[#A8A8A8] hover:text-[#1A1A1A] underline underline-offset-4"
                    data-testid="checkout-restart-intent-btn"
                  >
                    Iniciar novo pedido
                  </button>
                </div>
              </div>
            )}

            {step === "approved" && (
              <div className="bg-white p-10 md:p-14 text-center" data-testid="checkout-approved">
                <div className="w-12 h-px bg-[#B8A88A] mx-auto mb-8"/>
                <p className="overline text-[#B8A88A] mb-4">Pedido confirmado</p>
                <h2 className="font-display text-4xl md:text-5xl mb-5 leading-tight">Parabéns pela compra!</h2>
                <p className="text-[#4A4A4A] leading-relaxed max-w-md mx-auto mb-8">
                  Sua peça foi reservada com sucesso. Em instantes você receberá um e-mail de confirmação
                  com os detalhes do pedido e os próximos passos para o rastreio.
                </p>
                <p className="text-sm text-[#6B6B6B] italic max-w-md mx-auto mb-10">
                  Obrigada pela confiança — esperamos que esta peça seja a realização de um sonho.
                </p>
                {paymentResult?.mp_payment_id && (
                  <div className="overline text-xs text-[#A8A8A8] mb-8" data-testid="checkout-order-id">
                    Código do pedido · #{String(paymentResult.mp_payment_id).slice(-8).toUpperCase()}
                  </div>
                )}
                <div className="flex justify-center">
                  <button onClick={() => nav("/")} className="btn-primary" data-testid="checkout-approved-home-btn">Voltar para a loja</button>
                </div>
                {/* Guest checkout only — nudge the buyer to save their data for
                    the next purchase. Pre-fills the register form via URL
                    params so they don't retype anything. */}
                {!user && checkoutEmail && (
                  <div
                    className="mt-10 pt-8 border-t border-[#E8E4DF]"
                    data-testid="guest-create-account-cta"
                  >
                    <p className="text-sm text-[#4A4A4A] leading-relaxed max-w-md mx-auto">
                      Quer salvar seus dados para facilitar a próxima compra?{" "}
                      <Link
                        to={`/cadastro?email=${encodeURIComponent(checkoutEmail)}&name=${encodeURIComponent(form.name)}&phone=${encodeURIComponent(form.phone)}&cpf=${encodeURIComponent(form.cpf)}`}
                        className="underline underline-offset-4 text-[#1A1A1A] hover:text-[#B8A88A] transition-colors"
                        data-testid="guest-create-account-link"
                      >
                        Criar conta
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            )}

            {step === "pending" && (
              <div className="bg-white p-10 text-center space-y-4" data-testid="checkout-pending">
                <div className="w-12 h-px bg-[#B8A88A] mx-auto mb-6"/>
                <h2 className="font-display text-3xl mb-3">Quase lá!</h2>
                {paymentResult?.pix_qr_code_base64 ? (
                  <>
                    <p className="text-[#4A4A4A] mb-4">Escaneie o QR Code abaixo no app do seu banco para finalizar o PIX. Suas peças ficam reservadas até a confirmação.</p>
                    <img src={`data:image/png;base64,${paymentResult.pix_qr_code_base64}`} alt="PIX QR" className="mx-auto w-56 h-56 object-contain"/>
                    {paymentResult.pix_qr_code && (
                      <code className="block break-all text-xs bg-[#FAF9F7] p-3 select-all">{paymentResult.pix_qr_code}</code>
                    )}
                  </>
                ) : paymentResult?.boleto_url ? (
                  <a href={paymentResult.boleto_url} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex">Abrir boleto</a>
                ) : (
                  <p className="text-[#4A4A4A]">Aguardando confirmação do pagamento.</p>
                )}
              </div>
            )}
          </div>

          {/* Sidebar — resumo + selos. Hidden on the approved screen
              because the confirmation already shows the order summary, so
              the sacola becomes redundant noise. */}
          {step !== "approved" && (
          <aside className="space-y-6">
            <div className="bg-white p-6">
              <h3 className="font-display text-xl mb-4">Sua sacola</h3>
              <div className="space-y-4 mb-4">
                {items.map(({ product }) => (
                  <div key={product.id} className="flex gap-3">
                    <div className="w-14 h-14 bg-[#FAF9F7] flex-shrink-0">
                      {product.images?.[0] && <img src={FILE_URL(product.images[0])} alt="" className="w-full h-full object-cover"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      {(() => { const b = displayBrand(product); return b ? <p className="text-xs text-[#A8A8A8]">{b}</p> : null; })()}
                      <p className="text-sm truncate">{product.name}</p>
                      <p className="text-sm text-[#1A1A1A] mt-1">{formatBRL(product.price)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#E8E4DF] pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-[#6B6B6B]">
                  <span>Subtotal</span><span>{formatBRL(subtotal)}</span>
                </div>
                {shippingEstimate && (
                  <div
                    className="flex justify-between text-[#3E6A1E]"
                    data-testid="checkout-shipping-row"
                  >
                    <span>Frete estimado</span>
                    <span>
                      <s className="text-[#A8A8A8] mr-2">{formatBRL(shippingEstimate.estimated_brl)}</s>
                      <strong>GRÁTIS para você</strong>
                    </span>
                  </div>
                )}
                {shippingEstimate?.delivery_min_days && (
                  <p
                    className="text-xs text-[#6B6B6B] leading-relaxed"
                    data-testid="checkout-delivery-estimate"
                  >
                    Entrega estimada em {shippingEstimate.delivery_min_days} a{" "}
                    {shippingEstimate.delivery_max_days} dias úteis após a confirmação do pagamento.
                  </p>
                )}
                {totals?.coupon_code && totals.coupon_discount > 0 && (
                  <div
                    className="flex justify-between text-[#3E6A1E]"
                    data-testid="summary-coupon-row"
                  >
                    <span>Cupom {totals.coupon_code}</span>
                    <span>−{formatBRL(totals.coupon_discount)}</span>
                  </div>
                )}
                {totals?.giftcard_code && totals.giftcard_used > 0 && (
                  <div
                    className="flex justify-between text-[#3E6A1E]"
                    data-testid="summary-giftcard-row"
                  >
                    <span>Gift card {totals.giftcard_code}</span>
                    <span>−{formatBRL(totals.giftcard_used)}</span>
                  </div>
                )}
                {isPixSelected && pixDiscountPct > 0 && totals?.final_amount > 0 && (
                  <div
                    className="flex justify-between text-[#3E6A1E]"
                    data-testid="summary-pix-discount-row"
                  >
                    <span>Desconto PIX ({pixDiscountPct}%)</span>
                    <span>
                      −{formatBRL(totals.final_amount * (pixDiscountPct / 100))}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-display pt-2 border-t border-[#E8E4DF]">
                  <span>Total</span>
                  <span data-testid="summary-total">
                    {(() => {
                      const base = totals ? totals.final_amount : subtotal;
                      const value =
                        isPixSelected && pixDiscountPct > 0
                          ? base * (1 - pixDiscountPct / 100)
                          : base;
                      return formatBRL(value);
                    })()}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6">
              <h3 className="overline text-[#A8A8A8] mb-4">Compra segura</h3>
              <TrustBadges variant="stack" />
            </div>
          </aside>
          )}
        </div>
      </div>

      {/* Shared consultora modal — opened from the PaymentBrick "Precisa de
          ajuda?" CTA whenever a card attempt fails. Same component used on
          home, Try-On invalid_photo and result screens. */}
      <ConsultoraModal open={consultoraOpen} onOpenChange={setConsultoraOpen} />
    </div>
  );
}
